import { Button, FormInstance, message } from "antd"
import { useEffect, useState } from "react"
import {
  AppBackgroundData,
  DataSettingItem,
  EmailData,
  FileUploadData,
  FileUploadDataLP,
  GeneralInformationData,
  GeneralInformationDataLP,
  MarketingSettingData,
  PayloadSetting,
  PopupData,
  SocialLinksData,
} from "~/types/types"
import { t } from "../../../languages/i18n"
import useSettingService from "../../settings/service/settingService"
import Email from "./Email"
import FacebookAppID from "./FacebookAppID"
import FileUpload from "./FileUpload"
import FileUploadLP from "./FileUploadLP"
import GeneralInformation from "./GeneralInformation"
import GeneralInformationLP from "./GeneralInformationLandingPage"
import GoogleAnalyticsCode from "./GoogleAnalyticsCode"
import GoogleAnalyticsCodeLP from "./GoogleAnalyticsCodeLP"
import HeaderScriptsLandingPage from "./HeaderScripsLandingPage"
import HeaderScripts from "./HeaderScripts"
import MarketingInformation from "./MarketingInformation"
import Popup from "./Popup"
import SettingAppBackground from "./SettingAppBackground"
import SocialLink from "./SocialLinks"

const GeneralSetting = () => {
  const [generalInfo, setGeneralInfo] = useState<GeneralInformationData>({
    websiteName: "",
    websiteDescription: "",
    tags: [],
  })
  const [fileUploadData, setFileUploadData] = useState<FileUploadData>({
    structuredDataUrl: "",
    metaImageUrl: "",
    faviconUrl: "",
  })
  const [gaCode, setGaCode] = useState("")
  const [facebookAppId, setFacebookAppId] = useState("")
  const [headerScripts, setHeaderScripts] = useState("")
  const [socialLinks, setSocialLinks] = useState<SocialLinksData>({
    facebook: "",
    instagram: "",
    linkedin: "",
    tiktok: "",
    youtube: "",
  })
  const handleSocialLinksUpdate: React.Dispatch<React.SetStateAction<SocialLinksData>> = update => {
    const newState = typeof update === "function" ? update(socialLinks) : update
    setSocialLinks(newState as SocialLinksData)
  }
  const [emailData, setEmailData] = useState<EmailData>({ contactEmail: "", adminEmail: "" })
  const [popupInformation, setPopupInformation] = useState<MarketingSettingData>({
    displayTime: "",
    hiddenTime: "",
    content: "",
    image: [],
  })
  const [popup, setPopup] = useState<PopupData>({
    displayTimePopup: "",
    hiddenTimePopup: "",
    templatePopup: "",
  })
  const [generalInfoLP, setGeneralInfoLP] = useState<GeneralInformationDataLP>({
    websiteNameLP: "",
    websiteDescriptionLP: "",
    tagsLP: [],
  })
  const [fileUploadDataLP, setFileUploadDataLP] = useState<FileUploadDataLP>({
    faviconUrlLP: "",
  })
  const [headerScriptsLP, setHeaderScriptsLP] = useState("")
  const [gaCodeLP, setGaCodeLP] = useState("")
  const [appBackgroundData, setAppBackgroundData] = useState<AppBackgroundData>({
    backgroundImageUrl: "",
  })

  const { createSettings, checkKeyExists, updateSetting, getAllSettings } = useSettingService()

  useEffect(() => {
    const loadSettings = async () => {
      const response = await getAllSettings()

      if (response?.data) {
        response.data.forEach((setting: DataSettingItem) => {
          const { key, value } = setting.attributes
          switch (key) {
            case "websiteName":
              setGeneralInfo(prev => ({ ...prev, websiteName: value }))
              break
            case "websiteDescription":
              setGeneralInfo(prev => ({ ...prev, websiteDescription: value }))
              break
            case "keywords":
              setGeneralInfo(prev => ({ ...prev, tags: JSON.parse(value) }))
              break
            case "structuredData":
              setFileUploadData(prev => ({ ...prev, structuredDataUrl: value }))
              break
            case "metaImage":
              setFileUploadData(prev => ({ ...prev, metaImageUrl: value }))
              break
            case "favicon":
              setFileUploadData(prev => ({ ...prev, faviconUrl: value }))
              break
            case "gaCode":
              setGaCode(value)
              break
            case "facebookAppID":
              setFacebookAppId(value)
              break
            case "headerScripts":
              setHeaderScripts(value)
              break
            case "facebookLink":
              setSocialLinks(prev => ({ ...prev, facebook: value }))
              break
            case "instagramLink":
              setSocialLinks(prev => ({ ...prev, instagram: value }))
              break
            case "linkedInLink":
              setSocialLinks(prev => ({ ...prev, linkedin: value }))
              break
            case "tiktokLink":
              setSocialLinks(prev => ({ ...prev, tiktok: value }))
              break
            case "youtubeLink":
              setSocialLinks(prev => ({ ...prev, youtube: value }))
              break
            case "contactEmail":
              setEmailData(prev => ({ ...prev, contactEmail: value }))
              break
            case "adminEmail":
              setEmailData(prev => ({ ...prev, adminEmail: value }))
              break
            case "popupDisplayTime":
              setPopupInformation(prev => ({ ...prev, displayTime: value }))
              break
            case "popupHiddenTime":
              setPopupInformation(prev => ({ ...prev, hiddenTime: value }))
              break
            case "popupContent":
              setPopupInformation(prev => ({ ...prev, content: value }))
              break
            case "popupImages":
              setPopupInformation(prev => ({ ...prev, image: JSON.parse(value) }))
              break
            case "popupDisplayTimePopup":
              setPopup(prev => ({ ...prev, displayTimePopup: value }))
              break
            case "popupHiddenTimePopup":
              setPopup(prev => ({ ...prev, hiddenTimePopup: value }))
              break
            case "popupTemplatePopup":
              setPopup(prev => ({ ...prev, templatePopup: value }))
              break
            case "websiteNameLP":
              setGeneralInfoLP(prev => ({ ...prev, websiteNameLP: value }))
              break
            case "websiteDescriptionLP":
              setGeneralInfoLP(prev => ({ ...prev, websiteDescriptionLP: value }))
              break
            case "faviconLP":
              setFileUploadDataLP(prev => ({ ...prev, faviconUrlLP: value }))
              break
            case "headerScriptsLP":
              setHeaderScriptsLP(value)
              break
            case "gaCodeLP":
              setGaCodeLP(value)
              break
            case "appBackgroundImage":
              setAppBackgroundData(prev => ({ ...prev, backgroundImageUrl: value || null }))
              break
          }
        })
      }
    }
    loadSettings()
  }, [])

  const createOrUpdateSetting = async (key: string, value: string) => {
    // Allow saving empty values to handle deletion cases
    if (value === undefined) {
      return
    }

    try {
      const existingSettings = await checkKeyExists(key)
      const payload: PayloadSetting = {
        key,
        value,
        description: key,
      }

      if (existingSettings?.data?.length > 0) {
        await updateSetting(existingSettings.data[0].id, payload)
      } else {
        const dummyForm = {
          resetFields: () => {},
        } as FormInstance

        await createSettings(payload, dummyForm)
      }
    } catch (error) {
      message.error(`Failed to save setting: ${key}`)
      throw error
    }
  }

  const handleSave = async () => {
    message.loading(t("Loading..."))
    try {
      await createOrUpdateSetting("websiteName", generalInfo.websiteName)
      await createOrUpdateSetting("websiteDescription", generalInfo.websiteDescription)
      if (generalInfo.tags.length > 0) {
        const keywordsString = JSON.stringify(generalInfo.tags)
        await createOrUpdateSetting("keywords", keywordsString)
      }

      await createOrUpdateSetting("structuredData", fileUploadData.structuredDataUrl ?? "")
      await createOrUpdateSetting("metaImage", fileUploadData.metaImageUrl ?? "")
      await createOrUpdateSetting("favicon", fileUploadData.faviconUrl ?? "")

      await createOrUpdateSetting("gaCode", gaCode)
      await createOrUpdateSetting("facebookAppID", facebookAppId)
      await createOrUpdateSetting("headerScripts", headerScripts)

      await createOrUpdateSetting("facebookLink", socialLinks.facebook ?? "")
      await createOrUpdateSetting("instagramLink", socialLinks.instagram ?? "")
      await createOrUpdateSetting("linkedInLink", socialLinks.linkedin ?? "")
      await createOrUpdateSetting("tiktokLink", socialLinks.tiktok ?? "")
      await createOrUpdateSetting("youtubeLink", socialLinks.youtube ?? "")

      await createOrUpdateSetting("contactEmail", emailData.contactEmail)
      await createOrUpdateSetting("adminEmail", emailData.adminEmail)
      await createOrUpdateSetting("popupDisplayTime", popupInformation.displayTime.toString())
      await createOrUpdateSetting("popupHiddenTime", popupInformation.hiddenTime.toString())
      await createOrUpdateSetting("popupContent", popupInformation.content)

      await createOrUpdateSetting("popupDisplayTimePopup", popup.displayTimePopup.toString())
      await createOrUpdateSetting("popupHiddenTimePopup", popup.hiddenTimePopup.toString())
      await createOrUpdateSetting("popupTemplatePopup", popup.templatePopup.toString())
      await createOrUpdateSetting("gaCodeLP", gaCodeLP)

      if (popupInformation.image.length > 0) {
        const imagesString = JSON.stringify(popupInformation.image)
        await createOrUpdateSetting("popupImages", imagesString)
      }

      await createOrUpdateSetting("websiteNameLP", generalInfoLP.websiteNameLP)
      await createOrUpdateSetting("websiteDescriptionLP", generalInfoLP.websiteDescriptionLP)
      if (generalInfoLP.tagsLP.length > 0) {
        const keywordsString = JSON.stringify(generalInfoLP.tagsLP)
        await createOrUpdateSetting("keywords", keywordsString)
      }
      await createOrUpdateSetting("faviconLP", fileUploadDataLP.faviconUrlLP ?? "")
      await createOrUpdateSetting("headerScriptsLP", headerScriptsLP)
      await createOrUpdateSetting("appBackgroundImage", appBackgroundData.backgroundImageUrl ?? "")

      message.success(t("All settings saved successfully!"))
    } catch (error) {
      message.error(t("Failed to save some settings. Please try again."))
    }
  }
  return (
    <div className="w-full flex flex-col gap-4 p-4">
      <div className="flex flex-row w-full justify-between items-center">
        <h2 className="text-xl font-bold uppercase">{t("General Setting")}</h2>
        <Button type="primary" onClick={handleSave}>
          {t("Save")}
        </Button>
      </div>
      <GeneralInformation generalInfo={generalInfo} setGeneralInfo={setGeneralInfo} />
      <FileUpload fileUploadData={fileUploadData} setFileUploadData={setFileUploadData} />
      <GoogleAnalyticsCode gaCode={gaCode} setGaCode={setGaCode} />
      <FacebookAppID facebookAppId={facebookAppId} setFacebookAppId={setFacebookAppId} />
      <HeaderScripts headerScripts={headerScripts} setHeaderScripts={setHeaderScripts} />
      <SocialLink socialLinks={socialLinks} setSocialLinks={handleSocialLinksUpdate} />
      <Email emailData={emailData} setEmailData={setEmailData} />
      <MarketingInformation popupInformation={popupInformation} setPopupInformation={setPopupInformation} />
      <Popup popup={popup} setPopup={setPopup} />
      <GeneralInformationLP generalInfoLP={generalInfoLP} setGeneralInfoLP={setGeneralInfoLP} />
      <FileUploadLP fileUploadDataLP={fileUploadDataLP} setFileUploadDataLP={setFileUploadDataLP} />
      <HeaderScriptsLandingPage headerScriptsLP={headerScriptsLP} setHeaderScriptsLP={setHeaderScriptsLP} />
      <GoogleAnalyticsCodeLP gaCodeLP={gaCodeLP} setGaCodeLP={setGaCodeLP} />
      <SettingAppBackground appBackgroundData={appBackgroundData} setAppBackgroundData={setAppBackgroundData} />
    </div>
  )
}

export default GeneralSetting
